import { Building2 } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL } from '@/lib/back-nav';

import { PageChrome } from '@/components/layout/page-chrome';
import { useActiveCompany } from '@/lib/active-company-context';
import { useMe } from '@/lib/me';
import { NewDocumentModal } from '@/features/document-registration/components/NewDocumentModal';
import { RequestModal } from '@/features/document-requests/components/RequestModal';
import { RequestsPanel } from '@/features/document-requests/components/RequestsPanel';
import { useRequestSummary } from '@/features/document-requests/use-document-requests';
import { useRequestsUrl, type MasterListView } from '@/features/document-requests/use-requests-url';
import { SegTabs } from '@/components/ui/SegTabs';
import { segPanelId, segTabId } from '@/components/ui/seg-tabs-ids';
import { plural } from '@/lib/plural';
import { ImportModal } from '@/features/document-migration/components/ImportModal';
import { usePermission } from '@/hooks/usePermission';

import { MasterListBody } from './components/MasterListBody';
import { MasterListActions } from './components/MasterListActions';
import { MasterListGate } from './components/MasterListGate';
import { MasterListEmpty } from './components/MasterListEmpty';
import { ColumnsMenu } from './components/ColumnsMenu';
import { FilterBar } from './components/FilterBar';
import { isFiltered, toQueryParams, useUrlFilters } from './use-url-filters';
import { useFilterCatalogs } from './use-filter-catalogs';
import { useMasterListAccess } from './use-master-list-access';
import { exportSearch, useMasterList } from './use-master-list';
import { useVisibleColumns } from './use-visible-columns';
import { StatePanel } from '@/components/ui/StatePanel';
import { ApiError } from '@/lib/api';

const LIMIT = 20;
const TABS_ID = 'lista-maestra';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Gestión Documental' },
  { label: 'Lista Maestra', strong: true },
];

export function MasterList(): ReactElement {
  const { filters, setSegment, setQuery, setPage, setFilters, clearFilter, clearAll } =
    useUrlFilters();
  const canRegister = usePermission('quality:document:register');
  const canImport = usePermission('system:historical:load');
  // Solicitudes de documento (G3-1): pestaña dentro de la Lista Maestra, sin página propia.
  const canRequest = usePermission('quality:document:request');
  // Quien lee la Lista Maestra puede tener solicitudes propias (dueño de proceso, M4).
  const canReadList = usePermission('quality:master-list:read');
  const views = useRequestsUrl();
  const pending = useRequestSummary(canRegister).data ?? 0;
  // Solicitar exige leer la Lista Maestra (compuerta de G4). Un proveedor con acceso
  // temporal ve los documentos de su acceso, nunca la bandeja, ni siquiera por enlace.
  const showTabs = canRegister || canRequest || canReadList;
  const onRequests = showTabs && views.view === 'solicitudes';
  const [requesting, setRequesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  // Al limpiar todo se remonta la barra: descarta una búsqueda a medio escribir (debounce).
  const [filterBarKey, setFilterBarKey] = useState(0);
  const { activeCompanyId, isLoading: companyLoading } = useActiveCompany();
  const me = useMe();
  const visible = useVisibleColumns(activeCompanyId, me.data?.data.auth_subject ?? null);
  // /me ya resolvió y no hay empresa activa (sin membresía o sin elegir): no hay lista que
  // cargar; se dice qué hacer en vez de un esqueleto infinito.
  const noCompany = !companyLoading && !activeCompanyId;
  const catalogs = useFilterCatalogs(activeCompanyId);

  const exportQuery = exportSearch(toQueryParams(filters, LIMIT));
  const access = useMasterListAccess();
  const query = useMasterList(toQueryParams(filters, LIMIT), {
    companyId: activeCompanyId,
    enabled: Boolean(activeCompanyId) && access.state === 'granted',
  });
  const rows = query.data?.data ?? [];
  const customFieldColumns = query.data?.meta.customFieldColumns ?? [];
  // Los campos propios se ven por defecto; el selector guarda los que se ocultan.
  const shownCustom = customFieldColumns.filter((c) => !visible.hiddenCustom.includes(c.key));
  const total = query.data?.meta.total ?? 0;
  const busy = query.isFetching || !activeCompanyId;
  // G4 (FUN-06): sin acceso confirmado (o 403 del backend) la pantalla no se nombra.
  const forbidden = query.error instanceof ApiError && query.error.status === 403;
  if (access.state !== 'granted' || forbidden) {
    const state = forbidden ? 'denied' : access.state === 'granted' ? 'denied' : access.state;
    return (
      <MasterListGate
        state={state}
        error={access.error}
        onRetry={access.retry}
        retrying={access.retrying}
      />
    );
  }

  return (
    <>
      <PageChrome breadcrumb={BREADCRUMB} navContext="Lista maestra" />

      <BackLink target={TO_PANEL} />

      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Lista Maestra de Documentos</h1>
          <p style={{ maxWidth: '70ch' }}>
            Documentos controlados de la empresa activa: versión vigente, estado, normas aplicables
            y próxima revisión.
          </p>
        </div>
        {!noCompany && (
          <MasterListActions
            listActions={!onRequests}
            exportQuery={exportQuery}
            columns={
              <ColumnsMenu
                columns={visible.columns}
                customColumns={customFieldColumns}
                customKeys={shownCustom.map((c) => c.key)}
                onToggle={visible.toggle}
                onToggleCustom={visible.toggleCustom}
                onReset={visible.reset}
              />
            }
            {...(canImport ? { onImport: () => setImporting(true) } : {})}
            {...(canRequest ? { onRequest: () => setRequesting(true) } : {})}
            {...(canRegister ? { onCreate: () => setCreating(true) } : {})}
          />
        )}
      </div>
      {creating && <NewDocumentModal open onClose={() => setCreating(false)} />}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
      {requesting && <RequestModal mode={{ kind: 'nuevo' }} onClose={() => setRequesting(false)} />}

      {noCompany ? (
        <StatePanel
          icon={<Building2 />}
          title="Selecciona una empresa activa"
          role="status"
          className="rise d1"
        >
          Elige una empresa activa en la barra superior.
        </StatePanel>
      ) : (
        <>
          {showTabs && (
            <SegTabs<MasterListView>
              tabs={[
                { value: 'documentos', label: 'Documentos' },
                {
                  value: 'solicitudes',
                  label:
                    pending > 0
                      ? `Solicitudes (${plural(pending, 'pendiente', 'pendientes')})`
                      : 'Solicitudes',
                },
              ]}
              value={views.view}
              onChange={views.setView}
              label="Vista de la Lista Maestra"
              idPrefix={TABS_ID}
            />
          )}
          <div
            className="tabpanel"
            role={showTabs ? 'tabpanel' : undefined}
            id={showTabs ? segPanelId(TABS_ID, views.view) : undefined}
            aria-labelledby={showTabs ? segTabId(TABS_ID, views.view) : undefined}
          >
            {onRequests ? (
              <RequestsPanel canSeeAll={canRegister} />
            ) : (
              <>
                <FilterBar
                  key={filterBarKey}
                  className="rise d1"
                  filters={filters}
                  catalogs={catalogs}
                  onSegment={setSegment}
                  onQuery={setQuery}
                  onSetFilters={setFilters}
                  onClearFilter={clearFilter}
                />
                <MasterListBody
                  // Datos de la vista anterior sin filas (p. ej. al limpiar un filtro sin
                  // resultados): esqueleto hasta que llegue la consulta nueva, no "empresa vacía".
                  isLoading={
                    !activeCompanyId ||
                    query.isLoading ||
                    (query.isPlaceholderData && rows.length === 0)
                  }
                  isError={query.isError}
                  error={query.error}
                  hasData={query.data !== undefined}
                  forbidden={forbidden}
                  rows={rows}
                  columns={visible.columns}
                  customColumns={shownCustom}
                  page={filters.page}
                  pageSize={LIMIT}
                  total={total}
                  busy={busy}
                  onPage={setPage}
                  onRetry={() => void query.refetch()}
                  empty={
                    <MasterListEmpty
                      filtered={isFiltered(filters)}
                      // Solo desde la página 2: en la primera, "Ir a la primera página" no haría nada.
                      pastEnd={total > 0 && filters.page > 1}
                      onCreate={canRegister ? () => setCreating(true) : undefined}
                      onClearFilters={() => {
                        clearAll();
                        setFilterBarKey((k) => k + 1);
                      }}
                      onFirstPage={() => setPage(1)}
                    />
                  }
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
